import {
  CloudUploadOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
  UserOutlined
} from '@ant-design/icons';
import { Button, Card, Col, Collapse, Divider, Form, Image, Input, List, message, Modal, Progress, Row, Select, Space, Tag, Typography, Upload } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import './FileUploader.less';
import UserGuide from './UserGuide';

// 导入示例图片

// 导入打码模块
import {
  convertPdfToImages,
  imageToFile,
  initializeOCREngine,
  isPdfFile,
  loadImageFromFile,
  maskSensitiveInfo
} from '../services/imageMasking.js';

// 导入会员服务和认证上下文
import { useAuth } from '../contexts/AuthContext';
import memberService from '../services/member';

const { Dragger } = Upload
const { Title, Text, Paragraph } = Typography
const { Option } = Select
const { Panel } = Collapse
const { Item: FormItem } = Form

const FileUploader = ({ 
  selectedFiles, 
  onFilesSelected, 
  onClearFiles, 
  onProcessFiles,
  isProcessing,
  disabled,
  pageType = 'home',
  onOcrStatusChange = () => {}
}) => {
  const { user, isAuthenticated } = useAuth();
  const [dragOver, setDragOver] = useState(false)
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const batchProcessingStopRef = useRef(false);
  const [memberInfo, setMemberInfo] = useState(null);
  const [permissionCheck, setPermissionCheck] = useState(null);

  
  // 使用Form管理所有字段
  const [form] = Form.useForm();
  
  // 控制基础信息字段的显示状态
  const [visibleBasicFields, setVisibleBasicFields] = useState({
    ageField: true,
    genderField: true, 
    registerField: true,
    caseNumberField: true,
    reportTimeField: true
  })

  // OCR和打码相关状态
  const [ocrEngine, setOcrEngine] = useState(null);
  const [isOcrReady, setIsOcrReady] = useState(false);
  const [ocrInitProgress, setOcrInitProgress] = useState(0);
  const [maskingProgress, setMaskingProgress] = useState(0);
  const [isMasking, setIsMasking] = useState(false);
  const [maskedFiles, setMaskedFiles] = useState([]);
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);
  
  // 通知父组件OCR状态变化
  useEffect(() => {
    onOcrStatusChange(isOcrReady);
  }, [isOcrReady, onOcrStatusChange]);

  // 文件上传进度相关状态
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [currentUploadFile, setCurrentUploadFile] = useState('');
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalFilesToUpload, setTotalFilesToUpload] = useState(0);

  // 打码进度相关状态
  const [maskingDetailProgress, setMaskingDetailProgress] = useState(0);
  const [maskingDetailStatus, setMaskingDetailStatus] = useState('');
  const [currentMaskingFile, setCurrentMaskingFile] = useState('');
  const [maskingStep, setMaskingStep] = useState('');
  const [processedCount, setProcessedCount] = useState(0);
  const [totalFilesToMask, setTotalFilesToMask] = useState(0);
  
  // 新增：图片预览控制状态
  const [showAllPreviews, setShowAllPreviews] = useState(false);
  const [isStartingProcess, setIsStartingProcess] = useState(false);
  const [overallMaskingProgress, setOverallMaskingProgress] = useState(0);
  const [showMaskingProgress, setShowMaskingProgress] = useState(false);
  
  // 新增：弹窗预览相关状态
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewImage, setPreviewImage] = useState('');

  // 基础字段配置
  const basicFieldConfigs = {
    ageField: { label: '年龄字段', tooltip: '报告中表示患者年龄的字段名称', placeholder: '例如：年龄', defaultValue: '年龄' },
    genderField: { label: '性别字段', tooltip: '报告中表示患者性别的字段名称', placeholder: '例如：性别', defaultValue: '性别' },
    registerField: { label: '登记号字段', tooltip: '报告中表示登记号/ID的字段名称', placeholder: '例如：登记号', defaultValue: '登记号' },
    caseNumberField: { label: '病案号字段', tooltip: '报告中表示病案号的字段名称', placeholder: '例如：病案号', defaultValue: '病案号' },
    reportTimeField: { label: '报告时间字段', tooltip: '报告中表示报告时间的字段名称', placeholder: '例如：报告时间', defaultValue: '报告时间' }
  }

  // 初始化OCR引擎
  useEffect(() => {
    initializeOCREngineForComponent();
  }, []);

  // 注释掉原来的单次加载，现在由定时器统一处理
  // useEffect(() => {
  //   if (isAuthenticated) {
  //     loadMemberInfo();
  //   }
  // }, [isAuthenticated]);

  // 监听文件变化，检查权限
  useEffect(() => {
    if (selectedFiles.length > 0 && memberInfo) {
      checkUploadPermission();
    }
  }, [selectedFiles, memberInfo]);

  // 每10秒自动更新会员积分信息
  useEffect(() => {
    if (!isAuthenticated) return;

    // 立即加载一次（非静默模式，显示日志）
    loadMemberInfo(false);

    // 设置定时器，每10秒更新一次（静默模式，减少日志）
    const intervalId = setInterval(() => {
      loadMemberInfo(true); // 静默模式
    }, 10000); // 10秒 = 10000毫秒

    // 清理定时器
    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  const loadMemberInfo = async (silent = false) => {
    try {
      // 使用 refreshMemberInfo 而不是 getMemberInfo，这样会触发全局事件
      const info = await memberService.refreshMemberInfo();
      setMemberInfo(info);
      if (!silent) {
        console.log('会员信息已更新:', info);
      }
    } catch (error) {
      if (!silent) {
        console.error('加载会员信息失败:', error);
      }
      // 静默模式下不显示错误，避免定时器产生大量错误日志
    }
  };

  const checkUploadPermission = async () => {
    if (!memberInfo) return;
    
    try {
      const result = await memberService.checkMemberPermissions(selectedFiles.length);
      setPermissionCheck(result);
    } catch (error) {
      console.error('检查权限失败:', error);
    }
  };

  // 清理预览URL
  useEffect(() => {
    return () => {
      if (previewImage && previewImage.startsWith('blob:')) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  const initializeOCREngineForComponent = async () => {
    try {
      setOcrInitProgress(10);
      
      // 使用打码模块中的初始化函数
      const engine = await initializeOCREngine((progress) => {
        setOcrInitProgress(progress);
      });
      
      setOcrEngine(engine);
      setIsOcrReady(true);
      
      //message.success('OCR引擎初始化完成，支持本地打码功能');
      
    } catch (error) {
      message.error(`OCR引擎初始化失败: ${error.message}`);
      setIsOcrReady(false);
      setOcrInitProgress(0);
      
      // 提供降级提示
      message.warning('打码功能不可用，将跳过敏感信息处理步骤', 5);
    }
  };

  
  // 文件图标映射
  const getFileIcon = (file) => {
    const fileName = typeof file === 'string' ? file : file.name
    const extension = fileName.split('.').pop().toLowerCase()
    
    if (extension === 'pdf') {
      return <FilePdfOutlined className="file-icon pdf" />
    } else if (['jpg', 'jpeg', 'png'].includes(extension)) {
      return <FileImageOutlined className="file-icon image" />
    } else {
      return <FileOutlined className="file-icon" />
    }
  }
  
  // 处理文件选择
  const handleFileSelect = () => {
    // 检查登录状态
    if (!isAuthenticated) {
      message.warning('请先登录后再上传文件');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.jpg,.jpeg,.png';
    
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        let finalFiles = files;
        
        // 检查文件数量限制并处理
        if (memberInfo) {
          const maxAllowed = memberInfo.max_images;
          const currentTotal = selectedFiles.length + files.length;
          
          if (currentTotal > maxAllowed) {
            if (memberInfo.is_member) {
              // 会员用户：检查积分是否足够
              if (currentTotal > memberInfo.bean_coins) {
                message.warning(`积分不足，您只有${memberInfo.bean_coins}个积分，无法上传${currentTotal}张图片`);
                return;
              }
              // 如果积分足够，允许上传（会员没有数量限制）
            } else {
              // 非会员用户：自动清理多余的图片
              const availableSlots = Math.max(0, maxAllowed - selectedFiles.length);
              finalFiles = files.slice(0, availableSlots);
              
              if (finalFiles.length < files.length) {
                message.warning(`非会员用户单次最多只能上传${maxAllowed}张图片，已自动选择前${finalFiles.length}张图片`);
                message.info(`多余的${files.length - finalFiles.length}张图片已被过滤`);
              }
              
              if (finalFiles.length === 0) {
                message.warning('已达到上传数量限制，无法添加更多文件');
                return;
              }
            }
          }
        }
        
        // 合并新文件与现有文件，避免重复
        const newFiles = selectedFiles.length > 0 ? 
          [...selectedFiles, ...finalFiles.filter(file => 
            !selectedFiles.some(selectedFile => 
              selectedFile.name === file.name && selectedFile.size === file.size
            )
          )] : 
          finalFiles;
        
        onFilesSelected(newFiles);
        
        // 提示用户添加了多少个文件
        const addedCount = finalFiles.filter(file => 
          !selectedFiles.some(selectedFile => 
            selectedFile.name === file.name && selectedFile.size === file.size
          )
        ).length;
        
        if (addedCount > 0) {
          message.success(`成功添加 ${addedCount} 个文件`);
        } else if (finalFiles.length > 0) {
          message.info('选择的文件已存在，未添加重复文件');
        }
      }
    };
    
    input.click();
  }

  // 处理文件拖拽
  const handleFileDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
    
    if (disabled) {
      return
    }
    
    // 检查登录状态
    if (!isAuthenticated) {
      message.warning('请先登录后再上传文件');
      return;
    }
    
    const files = Array.from(e.dataTransfer.files)
    const validFiles = files.filter(file => {
      const extension = file.name.split('.').pop().toLowerCase()
      return ['pdf', 'jpg', 'jpeg', 'png'].includes(extension)
    })
    
    if (validFiles.length === 0) {
      message.warning('请选择支持的文件格式：PDF、JPG、PNG')
      return
    }
    
    if (validFiles.length !== files.length) {
      message.warning(`已过滤掉 ${files.length - validFiles.length} 个不支持的文件`)
    }
    
    // 检查文件数量限制并处理
    let finalValidFiles = validFiles;
    if (memberInfo) {
      const maxAllowed = memberInfo.max_images;
      const currentTotal = selectedFiles.length + validFiles.length;
      
      if (currentTotal > maxAllowed) {
        if (memberInfo.is_member) {
          // 会员用户：检查积分是否足够
          if (currentTotal > memberInfo.bean_coins) {
            message.warning(`积分不足，您只有${memberInfo.bean_coins}个积分，无法上传${currentTotal}张图片`);
            return;
          }
          // 如果积分足够，允许上传（会员没有数量限制）
        } else {
          // 非会员用户：自动清理多余的图片
          const availableSlots = Math.max(0, maxAllowed - selectedFiles.length);
          finalValidFiles = validFiles.slice(0, availableSlots);
          
          if (finalValidFiles.length < validFiles.length) {
            message.warning(`非会员用户单次最多只能上传${maxAllowed}张图片，已自动选择前${finalValidFiles.length}张图片`);
            message.info(`多余的${validFiles.length - finalValidFiles.length}张图片已被过滤`);
          }
          
          if (finalValidFiles.length === 0) {
            message.warning('已达到上传数量限制，无法添加更多文件');
            return;
          }
        }
      }
    }
    
    // 在 Web 环境中，直接使用文件对象
    const newFiles = selectedFiles.length > 0 ? 
      [...selectedFiles, ...finalValidFiles.filter(file => 
        !selectedFiles.some(selectedFile => 
          selectedFile.name === file.name && selectedFile.size === file.size
        )
      )] : 
      finalValidFiles
    
    onFilesSelected(newFiles)
    message.success(`成功添加 ${finalValidFiles.length} 个文件`)
  }

  // 删除文件
  const handleRemoveFile = (fileToRemove) => {
    const fileName = typeof fileToRemove === 'string' ? fileToRemove.split(/[\\/]/).pop() : fileToRemove.name
    const newFiles = selectedFiles.filter(file => {
      const currentFileName = typeof file === 'string' ? file.split(/[\\/]/).pop() : file.name
      return currentFileName !== fileName
    })
    
    // 清理预览URL
    if (typeof fileToRemove === 'object' && fileToRemove.name) {
      // 如果删除的是当前预览的文件，关闭预览
      if (previewFile === fileToRemove) {
        handleClosePreview();
      }
    }
    
    onFilesSelected(newFiles)
    message.success(`已删除文件：${fileName}`)
  }

  // 重置所有状态
  const resetAllStates = () => {
    setMaskingProgress(0);
    setIsMasking(false);
    setMaskedFiles([]);
    setCurrentProcessingIndex(0);
    setMaskingDetailProgress(0);
    setMaskingDetailStatus('');
    setCurrentMaskingFile('');
    setMaskingStep('');
    setProcessedCount(0);
    setTotalFilesToMask(0);
    setIsStartingProcess(false);
    setOverallMaskingProgress(0);
    setShowMaskingProgress(false);
    
    // 清理预览URL
    if (previewImage && previewImage.startsWith('blob:')) {
      URL.revokeObjectURL(previewImage);
    }
    
    // 重置预览状态
    setPreviewVisible(false);
    setPreviewFile(null);
    setPreviewTitle('');
    setPreviewImage('');
  }

  // 处理输入框的值变化，阻止输入空格
  const handleInputChange = (e, onChange) => {
    const value = e.target.value;
    // 阻止输入空格
    const noSpaceValue = value.replace(/\s/g, '');
    onChange(noSpaceValue);
  };

  // 处理输入框失焦，自动trim前后空格（作为备用处理）
  const handleInputBlur = (e, onChange) => {
    const value = e.target.value;
    const trimmedValue = value.trim();
    if (trimmedValue !== value) {
      onChange(trimmedValue);
    }
  };

  // 处理单个文件的打码 - 返回打码信息和预览（支持PDF转图片）
  const processFileForMasking = async (file, progressCallback, maxImageLimit = 2000) => {
    if (!isOcrReady || !ocrEngine) {
      return [{
        originalFile: file,
        maskedFile: file,
        previewUrl: null,
        isMasked: false,
        error: 'OCR引擎未就绪',
        name: file.name
      }];
    }

    try {
      let imagesToProcess = [];
      
      if (isPdfFile(file)) {
        // PDF文件：转换为多个图片页面
        progressCallback && progressCallback('PDF转换中...', 0);
        
        try {
          const pdfImages = await convertPdfToImages(file, (progress, message) => {
            setMaskingProgress(Math.round(progress * 0.5)); // PDF转换占总进度的50%
            progressCallback && progressCallback('PDF转换中...', Math.round(progress * 0.3)); // PDF转换占单文件进度的30%
          });
          
          // PDF转换完成后，检查页面数量限制
          let imagesToConvert = pdfImages;
          if (pdfImages.length > maxImageLimit) {
            imagesToConvert = pdfImages.slice(0, maxImageLimit);
            message.warning(`PDF包含${pdfImages.length}页，非会员用户限制为${maxImageLimit}页，只处理前${maxImageLimit}页`, 5);
          }
          
          // 将PDF页面转换为处理格式
          for (const pdfPage of imagesToConvert) {
            imagesToProcess.push({
              image: pdfPage.image,
              fileName: pdfPage.fileName,
              title: pdfPage.title,
              pageNumber: pdfPage.pageNumber,
              originalWidth: pdfPage.originalWidth,
              originalHeight: pdfPage.originalHeight
            });
          }
        } catch (pdfError) {
          console.error('PDF转换失败:', pdfError);
          return [{
            originalFile: file,
            maskedFile: file,
            previewUrl: null,
            isMasked: false,
            error: `PDF转换失败: ${pdfError.message}`,
            name: file.name,
            isPdf: true
          }];
        }
      } else if (isImageFile(file.name)) {
        // 单个图片文件
        // 图片文件直接处理
        const image = await loadImageFromFile(file);
        imagesToProcess.push({
          image: image,
          fileName: file.name,
          title: '图片',
          pageNumber: 1,
          originalWidth: image.width,
          originalHeight: image.height
        });
      } else {
        // 不支持的文件类型
        return [{
          originalFile: file,
          maskedFile: file,
          previewUrl: null,
          isMasked: false,
          error: '不支持的文件类型',
          name: file.name
        }];
      }

      // 处理所有图片页面
      const results = [];
      for (let i = 0; i < imagesToProcess.length; i++) {
        const { image, fileName, title, pageNumber, originalWidth, originalHeight } = imagesToProcess[i];
        
        // 更新进度
        const baseProgress = isPdfFile(file) ? 30 : 0; // 如果是PDF，前30%已用于转换
        const currentPageProgress = (i / imagesToProcess.length) * 70; // 图片处理占70%
        
        try {
          // OCR识别阶段
          const ocrStepProgress = baseProgress + currentPageProgress + (0.3 * 70 / imagesToProcess.length);
          progressCallback && progressCallback(`OCR识别: ${title}`, Math.round(ocrStepProgress));
          
          const ocrResult = await ocrEngine.ocr(image);
          
          // 打码处理阶段
          const maskingStepProgress = baseProgress + currentPageProgress + (0.6 * 70 / imagesToProcess.length);
          progressCallback && progressCallback(`打码处理: ${title}`, Math.round(maskingStepProgress));
          
          const maskedImage = await maskSensitiveInfo(image, ocrResult);
          
          // 验证打码后图片的像素尺寸是否保持一致
          if (maskedImage.width !== originalWidth || maskedImage.height !== originalHeight) {
            // 记录像素尺寸变化 
          }
          
          // 文件转换阶段
          const fileStepProgress = baseProgress + currentPageProgress + (0.9 * 70 / imagesToProcess.length);
          progressCallback && progressCallback(`文件转换: ${title}`, Math.round(fileStepProgress));
          
          // 创建预览URL（使用原始尺寸）
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = maskedImage.width;
          canvas.height = maskedImage.height;
          
          // 设置最高质量绘制参数
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          ctx.drawImage(maskedImage, 0, 0);
          const previewUrl = canvas.toDataURL('image/png');
          
          // 将打码后的图片转换为File对象，保持原始像素尺寸
          // 为PNG格式生成新的文件名
          const pngFileName = fileName.replace(/\.[^.]+$/, '.png');
          const maskedFile = await imageToFile(maskedImage, pngFileName, 'image/png');
          
          // 页面处理完成
          const pageCompleteProgress = baseProgress + ((i + 1) / imagesToProcess.length) * 70;
          progressCallback && progressCallback(`✅ 完成: ${title}`, Math.round(pageCompleteProgress));
          
          results.push({
            originalFile: file,
            maskedFile: maskedFile, // 这是图片文件，不是PDF
            previewUrl: previewUrl,
            isMasked: true,
            name: pngFileName,
            title: title,
            pageNumber: pageNumber,
            originalWidth: originalWidth,
            originalHeight: originalHeight,
            isPdfPage: isPdfFile(file) // 标记是否来自PDF
          });
          
        } catch (error) {
          console.error(`❌ ${title}打码处理失败:`, error);
          
          // 如果打码失败，使用原始图片转换为文件
          // 为PNG格式生成新的文件名
          const pngFileName = fileName.replace(/\.[^.]+$/, '.png');
          const originalImageFile = await imageToFile(image, pngFileName, 'image/png');
          
          results.push({
            originalFile: file,
            maskedFile: originalImageFile, // 即使失败也转换为图片文件
            previewUrl: null,
            isMasked: false,
            error: error.message,
            name: pngFileName,
            title: title,
            pageNumber: pageNumber,
            originalWidth: originalWidth,
            originalHeight: originalHeight,
            isPdfPage: isPdfFile(file)
          });
        }
      }
      
      // 文件处理完成
      return results;
      
    } catch (error) {
      console.error('文件处理失败:', error);
      return [{
        originalFile: file,
        maskedFile: file,
        previewUrl: null,
        isMasked: false,
        error: error.message,
        name: file.name
      }];
    }
  };

  // 开始逐张打码处理
  const startMaskingProcess = async () => {
    if (!selectedFiles.length) return;
    
    setIsMasking(true);
    setMaskedFiles([]);
    setCurrentProcessingIndex(0);
    
    // 初始化打码进度状态
    setMaskingDetailProgress(0);
    setMaskingDetailStatus('准备开始打码...');
    setCurrentMaskingFile('');
    setMaskingStep('');
    setProcessedCount(0);
    setTotalFilesToMask(selectedFiles.length);
    
    const allResults = [];
    let processedImageCount = 0; // 追踪已处理的图片数量
    const maxImagesForUser = memberInfo?.is_member ? 2000 : 20; // 会员用户最多2000张化验单，非会员用户最多20张图片
    
    for (let i = 0; i < selectedFiles.length; i++) {
      setCurrentProcessingIndex(i);
      const baseProgress = (i / selectedFiles.length) * 100;
      setMaskingProgress(Math.round(baseProgress));
      
      // 更新详细进度信息
      const fileName = selectedFiles[i].name;
      setCurrentMaskingFile(fileName);
      setMaskingDetailStatus(`正在处理: ${fileName} (${i + 1}/${selectedFiles.length})`);
      setMaskingStep('文件分析中...');
      
      try {
        const results = await processFileForMasking(selectedFiles[i], (step, progress) => {
          // 文件内部处理进度回调
          setMaskingStep(step);
          // 计算总体进度：已完成文件进度 + 当前文件进度
          const totalProgress = (i / selectedFiles.length) * 100 + (progress / selectedFiles.length);
          setMaskingDetailProgress(Math.round(totalProgress));
        }, maxImagesForUser - processedImageCount); // 传入剩余可处理的图片数量
        
        // 检查是否超过限制
        const newImageCount = results.length;
        if (processedImageCount + newImageCount > maxImagesForUser) {
          // 只取前面的结果，剩余的丢弃
          const allowedCount = maxImagesForUser - processedImageCount;
          const allowedResults = results.slice(0, allowedCount);
          const skippedCount = results.length - allowedCount;
          
          allResults.push(...allowedResults);
          processedImageCount += allowedResults.length;
          
          // 提示用户
          if (skippedCount > 0) {
            message.warning(`非会员用户已达到20张图片限制，跳过了${skippedCount}个页面`, 5);
          }
          
          // 更新已处理的文件列表
          setMaskedFiles([...allResults]);
          setMaskingStep('✅ 已达到处理限制');
          break; // 停止处理后续文件
        } else {
          // 将所有结果添加到总列表中
          allResults.push(...results);
          processedImageCount += newImageCount;
          
          // 实时更新已处理的文件列表
          setMaskedFiles([...allResults]);
          
          // 更新已处理数量
          setProcessedCount(i + 1);
          setMaskingStep('✅ 处理完成');
        }
        
      } catch (error) {
        setMaskingStep('❌ 处理失败');
        message.error(`处理失败: ${selectedFiles[i].name} - ${error.message}`, 3);
        allResults.push({
          originalFile: selectedFiles[i],
          maskedFile: selectedFiles[i],
          previewUrl: null,
          isMasked: false,
          error: error.message,
          name: selectedFiles[i].name
        });
      }
    }
    
    setMaskingProgress(100);
    setMaskingDetailProgress(100);
    setIsMasking(false);
    setCurrentProcessingIndex(-1);
    setMaskingDetailStatus('打码处理完成！');
    setMaskingStep('');
    
    // 统计处理结果
    const maskedCount = allResults.filter(r => r.isMasked).length;
    const failedCount = allResults.filter(r => r.error && !r.isMasked).length;
    const pdfPagesCount = allResults.filter(r => r.isPdfPage).length;
    
    if (maskedCount > 0) {
      if (pdfPagesCount > 0) {
        message.success(`✅ 成功处理 ${selectedFiles.length} 个文件，生成 ${allResults.length} 个图片页面，其中 ${maskedCount} 个已打码`, 3);
      } else {
        message.success(`✅ 成功打码 ${maskedCount} 个文件`, 3);
      }
    }
    if (failedCount > 0) {
      message.warning(`⚠️ ${failedCount} 个页面打码失败`, 3);
    }
    
    // 延迟清理进度状态
    setTimeout(() => {
      setMaskingDetailProgress(0);
      setMaskingDetailStatus('');
      setCurrentMaskingFile('');
      setMaskingStep('');
      setProcessedCount(0);
      setTotalFilesToMask(0);
    }, 3000);
  };

  // 批量上传所有打码文件到单一任务
  const processBatchMaskedFiles = async () => {
    if (maskedFiles.length === 0) {
      message.warning('没有需要处理的文件');
      return;
    }
    
    setIsBatchProcessing(true);
    batchProcessingStopRef.current = false;
    
    // 初始化上传进度状态
    setUploadProgress(0);
    setUploadedCount(0);
    setTotalFilesToUpload(maskedFiles.length);
    setUploadStatus('准备中...');
    

    
    try {
      // 收集表单数据
      const values = form.getFieldsValue();
      const requiredPatientFields = [
        values.ageField, 
        values.genderField, 
        values.registerField, 
        values.caseNumberField,
        values.reportTimeField
      ].filter(Boolean);
      
      const customPatientFields = values.customPatientFields || [];
      const allPatientFields = [...requiredPatientFields, ...customPatientFields].filter(Boolean);
      
      const testItemFields = values.testItemFields || [];
      const projectFieldName = testItemFields[0] || '项目名称';
      const resultFieldName = testItemFields[1] || '结果';
      
      setUploadStatus('创建任务中...');
      
      // 提示用户处理开始，引导查看页面底部进度
      message.info('🚀 批量处理已开始，请查看页面底部的处理状态', 4);
      
      // 导入新的API函数
      const { createTask, uploadFileToTask, startTaskProcessing, getTaskStatus } = await import('../services/api.js');
      
      // 第一步：创建任务
      const taskData = {
        task_name: `打码文件批量处理_${new Date().toLocaleString()}`,
        doc_type: 'lab_routine',
        patient_fields: allPatientFields.join(','),
        project_field_name: projectFieldName,
        result_field_name: resultFieldName
      };
      
      const taskResponse = await createTask(taskData);
      const taskId = taskResponse.task_id;
      
      setUploadStatus(`任务创建成功: ${taskId.substring(0, 8)}...`);
      setUploadProgress(10); // 创建任务完成占10%
      
      // 简化任务状态验证 - 给数据库事务一些时间
      try {
        // 等待一小段时间确保数据库事务完成
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const taskStatusCheck = await getTaskStatus(taskId);
        if (!taskStatusCheck) {
          console.warn('任务状态查询失败，但继续尝试上传文件...');
          message.warning('任务状态验证失败，但将尝试继续上传文件', 3);
        } else if (taskStatusCheck.status !== 'pending') {
          console.warn(`任务状态为 ${taskStatusCheck.status}，但继续尝试上传...`);
        } else {
          console.log('✅ 任务状态验证成功');
        }
      } catch (statusError) {
        console.warn('任务状态检查失败，但继续上传流程:', statusError.message);
        message.warning(`任务状态检查失败: ${statusError.message}，但将尝试继续上传`, 3);
        // 不再直接返回，继续执行上传流程
      }
      
      // 第二步：串行上传所有文件
      const totalFiles = maskedFiles.length;
      let uploadedCount = 0;
      
      for (let i = 0; i < maskedFiles.length; i++) {
        if (batchProcessingStopRef.current) {
          setUploadStatus('用户停止了批量处理');
          return;
        }
        
        const maskedFileInfo = maskedFiles[i];
        const fileName = maskedFileInfo.name || `文件${i+1}`;
        const displayName = maskedFileInfo.title || fileName;
        
        try {
          setCurrentUploadFile(displayName);
          setUploadStatus(`上传中: ${displayName} (${uploadedCount + 1}/${totalFiles})`);
          
          // 简化上传前状态检查 - 减少不必要的验证失败
          try {
            const preUploadStatus = await getTaskStatus(taskId);
            if (!preUploadStatus) {
              console.warn(`文件 ${displayName}: 任务状态查询失败，但尝试上传`);
            } else if (preUploadStatus.status !== 'pending') {
              console.warn(`文件 ${displayName}: 任务状态为 ${preUploadStatus.status}，但尝试上传`);
            }
          } catch (preCheckError) {
            console.warn(`文件 ${displayName}: 上传前状态检查失败，但继续尝试上传:`, preCheckError.message);
            // 不再跳过文件，直接尝试上传
          }
          
          const uploadResult = await uploadFileToTask(taskId, maskedFileInfo.maskedFile);
          
          uploadedCount++;
          setUploadedCount(uploadedCount);
          
          // 更新进度：10% (创建任务) + 80% (上传文件) + 10% (开始处理)
          const uploadProgressPercent = 10 + (uploadedCount / totalFiles) * 80;
          setUploadProgress(Math.round(uploadProgressPercent));
          
          // 清理预览URL
          if (maskedFileInfo.previewUrl) {
            try {
              URL.revokeObjectURL(maskedFileInfo.previewUrl);
            } catch (e) {
              // 静默处理URL清理错误
            }
          }
          
        } catch (error) {
          const errorMessage = error.message || '未知错误';
          message.error(`上传失败: ${displayName} - ${errorMessage}`, 5);
          
          // 如果是权限或任务不存在错误，提供详细信息
          if (errorMessage.includes('不存在或无权限访问') || errorMessage.includes('任务不存在')) {
            message.warning('检测到任务权限问题，请尝试刷新页面或重新开始处理', 8);
            console.error('任务详细错误:', {
              taskId: taskId,
              fileName: displayName,
              error: errorMessage,
              uploadIndex: i,
              timestamp: new Date().toISOString()
            });
          }
          
          // 继续处理下一个文件，不中断整个流程
        }
      }
      
      if (uploadedCount === 0) {
        setUploadStatus('没有文件上传成功');
        message.error('没有文件上传成功，请检查网络连接或刷新页面重试', 8);
        return;
      }
      
      // 第三步：开始处理任务
      setUploadStatus(`文件上传完成 (${uploadedCount}/${totalFiles})，开始处理...`);
      setUploadProgress(90);
      
      const processingResponse = await startTaskProcessing(taskId);
      
      // 清空打码文件列表
      setMaskedFiles([]);
      
      // 通知上级组件设置任务ID和处理状态
      if (uploadedCount > 0 && onProcessFiles) {
        // 调用onProcessFiles来设置任务ID，并触发ProcessStatus组件显示
        console.log('🎯 通知上级组件设置任务ID:', taskId);
        onProcessFiles(null, allPatientFields.join(','), projectFieldName, resultFieldName, null, taskId);
      }
      
      setUploadStatus(`批量处理启动成功！共上传 ${uploadedCount} 个文件`);
      setUploadProgress(100);
      
      // 延迟清理上传进度状态，但保持批量处理状态
      setTimeout(() => {
        setUploadProgress(0);
        setUploadStatus('');
        setCurrentUploadFile('');
        setUploadedCount(0);
        setTotalFilesToUpload(0);
        // 不立即清理批量处理状态，让ProcessStatus组件接管
      }, 2000);
      
    } catch (error) {
      const errorMessage = error.message || '未知错误';
      setUploadStatus(`批量处理失败: ${errorMessage}`);
      
      // 提供用户友好的错误信息和解决建议
      if (errorMessage.includes('不存在或无权限访问')) {
        message.error('任务权限验证失败，请刷新页面后重新尝试', 8);
        message.info('建议：如果问题持续，请检查网络连接或联系管理员', 8);
      } else if (errorMessage.includes('网络')) {
        message.error('网络连接错误，请检查网络后重试', 5);
      } else {
        message.error(`批量处理失败: ${errorMessage}`, 5);
      }
      
      console.error('批量处理详细错误:', {
        error: errorMessage,
        maskedFilesCount: maskedFiles.length,
        timestamp: new Date().toISOString()
      });
    } finally {
      // 延迟清理批量处理状态，给ProcessStatus组件接管的时间
      setTimeout(() => {
        setIsBatchProcessing(false);
        batchProcessingStopRef.current = false;
      }, 1000);
    }
  };

  // 单张上传处理（保留用于单独处理）
  const uploadMaskedFile = async (maskedFileInfo, index) => {
    const fileName = maskedFileInfo.name || '未知文件';
    
    try {
      message.info(`📤 正在处理单个文件: ${fileName}`, 2);
      
      // 暂时保存当前的maskedFiles状态
      const originalMaskedFiles = [...maskedFiles];
      
      // 临时设置为只包含当前文件
      setMaskedFiles([maskedFileInfo]);
      
      // 调用批量处理逻辑
      await processBatchMaskedFiles();
      
      // 恢复原状态，但移除当前处理的文件
      setMaskedFiles(originalMaskedFiles.filter((_, i) => i !== index));
      
      message.success(`✅ ${fileName} 处理完成`, 2);
      
    } catch (error) {
      console.error('文件处理失败:', error);
      message.error(`❌ ${fileName} 处理失败: ${error.message}`, 3);
      
      // 出错时也要移除文件
      setMaskedFiles(prev => {
        const newFiles = prev.filter((_, i) => i !== index);
        // 移除出错文件
        return newFiles;
      });
      
      throw error;
    }
  };

  // 处理开始解析，获取所有字段并传递
  const handleStartProcessing = async () => {
    try {
      setIsStartingProcess(true);
      setShowMaskingProgress(true);
      setOverallMaskingProgress(0);
      
      // 收集所有必填字段和自定义字段
      const values = form.getFieldsValue();
      // 表单数据收集完成
      
      // 构建患者信息字段字符串
      const requiredPatientFields = [
        values.ageField, 
        values.genderField, 
        values.registerField, 
        values.caseNumberField,
        values.reportTimeField
      ].filter(Boolean);
      
      // 添加其他自定义患者字段
      const customPatientFields = values.customPatientFields || [];
      const allPatientFields = [...requiredPatientFields, ...customPatientFields].filter(Boolean);
      
      // 获取检测项目字段 - 分别处理项目名称和结果字段
      const testItemFields = values.testItemFields || [];
      const projectFieldName = testItemFields[0] || '项目名称';
      const resultFieldName = testItemFields[1] || '结果';
      
      // 先进行本地打码处理
      if (isOcrReady && ocrEngine && selectedFiles.length > 0) {
        message.info('🔒 开始本地打码处理，保护敏感信息...', 3);
        
        // 添加整体进度回调
        await startMaskingProcessWithOverallProgress();
      } else {
        // 如果OCR未就绪，显示明确的状态信息
        let statusMessage = '';
        if (!isOcrReady) {
          statusMessage = 'OCR引擎未就绪，跳过打码步骤';
        } else if (!ocrEngine) {
          statusMessage = 'OCR引擎未初始化，跳过打码步骤';
        } else if (selectedFiles.length === 0) {
          statusMessage = '没有选择文件';
        }
        
        if (statusMessage) {
          message.warning(`⚠️ ${statusMessage}`, 3);
        }
        
        // 直接使用原文件进行处理 - 使用新的API流程
        setOverallMaskingProgress(100);
        console.log('📤 没有OCR打码，直接处理原文件:', selectedFiles.length, '个文件');
        onProcessFiles(
          selectedFiles, // 传递原文件
          allPatientFields.join(','),
          projectFieldName,
          resultFieldName,
          null, // formData
          null  // existingTaskId
        );
      }
    } catch (error) {
      console.error('文件处理失败:', error);
      message.error('处理文件时发生错误，请重试');
      setIsMasking(false);
      setIsStartingProcess(false);
      setShowMaskingProgress(false);
    }
  }

  // 新增：带整体进度的打码处理
  const startMaskingProcessWithOverallProgress = async () => {
    if (!selectedFiles.length) return;
    
    setIsMasking(true);
    setMaskedFiles([]);
    setCurrentProcessingIndex(0);
    
    // 初始化打码进度状态
    setMaskingDetailProgress(0);
    setMaskingDetailStatus('准备开始打码...');
    setCurrentMaskingFile('');
    setMaskingStep('');
    setProcessedCount(0);
    setTotalFilesToMask(selectedFiles.length);
    setOverallMaskingProgress(5); // 开始时设为5%
    
    const allResults = [];
    
    for (let i = 0; i < selectedFiles.length; i++) {
      setCurrentProcessingIndex(i);
      const baseProgress = (i / selectedFiles.length) * 100;
      setMaskingProgress(Math.round(baseProgress));
      
      // 更新整体进度 (5% - 95%)
      const overallProgress = 5 + ((i / selectedFiles.length) * 90);
      setOverallMaskingProgress(Math.round(overallProgress));
      
      // 更新详细进度信息
      const fileName = selectedFiles[i].name;
      setCurrentMaskingFile(fileName);
      setMaskingDetailStatus(`正在处理: ${fileName} (${i + 1}/${selectedFiles.length})`);
      setMaskingStep('文件分析中...');
      
      try {
        const results = await processFileForMasking(selectedFiles[i], (step, progress) => {
          // 文件内部处理进度回调
          setMaskingStep(step);
          // 计算总体进度：已完成文件进度 + 当前文件进度
          const totalProgress = (i / selectedFiles.length) * 100 + (progress / selectedFiles.length);
          setMaskingDetailProgress(Math.round(totalProgress));
        });
        
        // 将所有结果添加到总列表中
        allResults.push(...results);
        
        // 实时更新已处理的文件列表
        setMaskedFiles([...allResults]);
        
        // 更新已处理数量
        setProcessedCount(i + 1);
        setMaskingStep('✅ 处理完成');
        
      } catch (error) {
        setMaskingStep('❌ 处理失败');
        message.error(`处理失败: ${selectedFiles[i].name} - ${error.message}`, 3);
        allResults.push({
          originalFile: selectedFiles[i],
          maskedFile: selectedFiles[i],
          previewUrl: null,
          isMasked: false,
          error: error.message,
          name: selectedFiles[i].name
        });
      }
    }
    
    setMaskingProgress(100);
    setMaskingDetailProgress(100);
    setOverallMaskingProgress(100);
    setIsMasking(false);
    setCurrentProcessingIndex(-1);
    setMaskingDetailStatus('打码处理完成！');
    setMaskingStep('');
    setIsStartingProcess(false);
    
    // 统计处理结果
    const maskedCount = allResults.filter(r => r.isMasked).length;
    const failedCount = allResults.filter(r => r.error && !r.isMasked).length;
    const pdfPagesCount = allResults.filter(r => r.isPdfPage).length;
    
    if (maskedCount > 0) {
      if (pdfPagesCount > 0) {
        message.success(`✅ 成功处理 ${selectedFiles.length} 个文件，生成 ${allResults.length} 个图片页面，其中 ${maskedCount} 个已打码`, 3);
      } else {
        message.success(`✅ 成功打码 ${maskedCount} 个文件`, 3);
      }
    }
    if (failedCount > 0) {
      message.warning(`⚠️ ${failedCount} 个页面打码失败`, 3);
    }
    
    // 延迟清理进度状态
    setTimeout(() => {
      setMaskingDetailProgress(0);
      setMaskingDetailStatus('');
      setCurrentMaskingFile('');
      setMaskingStep('');
      setProcessedCount(0);
      setTotalFilesToMask(0);
      setShowMaskingProgress(false);
      setOverallMaskingProgress(0);
    }, 3000);
  };

  // 删除字段
  const removeBasicField = (fieldName) => {
    setVisibleBasicFields(prev => ({
      ...prev,
      [fieldName]: false
    }))
    form.setFieldValue(fieldName, '')
  }

  // 恢复字段
  const restoreBasicField = (fieldName) => {
    setVisibleBasicFields(prev => ({
      ...prev,
      [fieldName]: true
    }))
    form.setFieldValue(fieldName, basicFieldConfigs[fieldName].defaultValue)
  }

  // 处理预览文件
  const handlePreviewFile = async (file) => {
    try {
      const fileName = typeof file === 'string' ? file.split(/[\\/]/).pop() : file.name;
      setPreviewTitle(fileName);
      setPreviewFile(file);
      
      // 如果是图片文件，创建预览URL
      if (typeof file === 'object' && isImageFile(file.name)) {
        const imageUrl = URL.createObjectURL(file);
        setPreviewImage(imageUrl);
      } else if (typeof file === 'string' && isImageFile(file)) {
        // 对于文件路径，直接使用路径
        setPreviewImage(file);
      } else {
        setPreviewImage('');
      }
      
      setPreviewVisible(true);
    } catch (error) {
      console.error('预览文件失败:', error);
      message.error('无法预览此文件');
    }
  };
  
  // 关闭预览
  const handleClosePreview = () => {
    setPreviewVisible(false);
    // 清理预览URL
    if (previewImage && previewImage.startsWith('blob:')) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewFile(null);
    setPreviewTitle('');
    setPreviewImage('');
  };
  
  // 判断是否是图片文件
  const isImageFile = (fileName) => {
    // 参数检查：确保fileName是字符串
    if (typeof fileName !== 'string') {
      console.warn('isImageFile: fileName必须是字符串类型，收到:', typeof fileName, fileName);
      return false;
    }
    
    try {
      const extension = fileName.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension);
    } catch (error) {
      console.error('isImageFile: 处理文件名时出错:', error, 'fileName:', fileName);
      return false;
    }
  };
  // 重新尝试初始化OCR引擎
  const debugOCRStatus = async () => {
    // 重新尝试OCR引擎初始化
    
    // 重置状态
    setIsOcrReady(false);
    setOcrEngine(null);
    setOcrInitProgress(0);
    
    message.info('🔄 正在重新初始化OCR引擎...', 3);
    
    // 检查ONNX Runtime状态
    if (!window.ort) {
      message.error('ONNX Runtime未加载，请刷新页面重试', 5);
      return;
    }
    
    // 检查OCR模型文件
    try {
      const dictResponse = await fetch('/ocr/ppocr_keys_v1.txt');
      if (!dictResponse.ok) {
        message.warning('OCR字典文件访问失败');
      }
    } catch (error) {
      message.warning('文件访问错误，请检查网络连接');
    }
    
    try {
      await initializeOCREngineForComponent();
      message.success('🎉 OCR引擎重新初始化成功！', 3);
      
    } catch (error) {
      message.error(`重新初始化失败: ${error.message}`, 5);
    }
  };

  return (
    <div>
      {/* OCR初始化进度 */}
      {ocrInitProgress > 0 && ocrInitProgress < 100 && (
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '12px' }}>
              <EyeInvisibleOutlined style={{ fontSize: '24px', color: '#1890ff', marginRight: '8px' }} />
              <span style={{ fontSize: '16px', fontWeight: 'bold' }}>正在初始化本地打码引擎...</span>
            </div>
            <Progress
              percent={ocrInitProgress}
              size="small"
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
            <div style={{ marginTop: '8px', color: '#666' }}>
              首次加载需要下载OCR模型文件，请耐心等待
            </div>
          </div>
        </Card>
      )}

      {/* OCR初始化成功后显示 */}
      {isOcrReady && (
      <Card style={{ marginBottom: '16px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
        <div style={{ textAlign: 'center', color: '#389e0d' }}>
          <EyeInvisibleOutlined style={{ fontSize: '20px', marginRight: '8px' }} />
          <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
            ✅ 本地打码引擎已就绪，将在上传前，对化验单进行隐私打码，保护敏感信息
          </span>
        </div>
      </Card>
      )}

      {/* 用户指南 */}
      <Card style={{ marginBottom: '16px' }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <UserGuide />
        </Col>
      </Row>
      </Card>

      {/* 文件上传Card */}
      <Card className="upload-card" variant="borderless">
      {/* 必填字段设置区域 */}
      <Card style={{ marginBottom: '24px' }}>
      <Title level={3} style={{ marginBottom: '24px' }}>
          开始使用
      </Title>
      <div className="required-fields-section">
        <Form
          form={form}
          layout="vertical"
          disabled={isProcessing}
          initialValues={{
            ageField: '年龄',
            genderField: '性别',
            registerField: '登记号',
            caseNumberField: '病案号',
            reportTimeField: '报告时间',
            customPatientFields: [],
            testItemFields: ['项目名称', '结果']
          }}
        >
          {/* 基础信息字段区域 */}
          <div className="field-section">
            <Divider orientation="left">
              <span style={{ color: '#1890ff', fontSize: '14px', fontWeight: '600' }}>
                患者基础信息字段
              </span>
            </Divider>
            <Paragraph style={{ marginBottom: '16px', fontSize: '12px', color: '#8c8c8c' }}>
              填写化验单中需要提取的用户基本信息字段，请参照样例绿框标注的字段名称
            </Paragraph>
          
            {/* 必填字段 */}
            <Row gutter={[16, 16]}>
              {Object.entries(visibleBasicFields).map(([fieldName, isVisible]) => (
                isVisible && (
                  <Col xs={24} sm={12} md={8} lg={6} key={fieldName}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <FormItem 
                        name={fieldName} 
                        label={basicFieldConfigs[fieldName].label} 
                        tooltip={basicFieldConfigs[fieldName].tooltip}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Input 
                          placeholder={basicFieldConfigs[fieldName].placeholder}
                          prefix={<UserOutlined />}
                          onChange={(e) => handleInputChange(e, (value) => form.setFieldValue(fieldName, value))}
                          onBlur={(e) => handleInputBlur(e, (value) => form.setFieldValue(fieldName, value))}
                        />
                      </FormItem>
                      <Button
                        onClick={() => removeBasicField(fieldName)}
                        type="text"
                        icon={<DeleteOutlined />}
                        style={{ marginTop: '30px' }}
                        title="删除此字段"
                      />
                    </div>
                  </Col>
                )
              ))}
            </Row>
            
            {/* 恢复已删除的字段 */}
            {Object.entries(visibleBasicFields).some(([_, isVisible]) => !isVisible) && (
              <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                <Typography.Text type="secondary" style={{ marginRight: '12px' }}>
                  恢复已删除字段：
                </Typography.Text>
                {Object.entries(visibleBasicFields).map(([fieldName, isVisible]) => (
                  !isVisible && (
                    <Button
                      key={fieldName}
                      size="small"
                      type="dashed"
                      onClick={() => restoreBasicField(fieldName)}
                      style={{ marginRight: '8px', marginBottom: '8px' }}
                      icon={<PlusOutlined />}
                    >
                      {basicFieldConfigs[fieldName].label}
                    </Button>
                  )
                ))}
              </div>
            )}
            
            {/* 自定义基础信息字段 */}
            <Form.List name="customPatientFields">
              {(fields, { add, remove }) => (
                <>
                  {fields.length > 0 && (
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                      {fields.map((field) => (
                        <Col xs={24} sm={12} md={8} lg={6} key={field.key}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <Form.Item
                              {...field}
                              label="自定义字段"
                              style={{ flex: 1, marginBottom: 0 }}
                              rules={[{ required: true, message: '请输入字段名称或删除此项' }]}
                            >
                              <Input 
                                placeholder="其他基础信息字段名称" 
                                prefix={<UserOutlined />}
                                onChange={(e) => handleInputChange(e, (value) => {
                                  const currentValues = form.getFieldValue('customPatientFields') || [];
                                  currentValues[field.name] = value;
                                  form.setFieldValue('customPatientFields', currentValues);
                                })}
                                onBlur={(e) => handleInputBlur(e, (value) => {
                                  const currentValues = form.getFieldValue('customPatientFields') || [];
                                  currentValues[field.name] = value;
                                  form.setFieldValue('customPatientFields', currentValues);
                                })}
                              />
                            </Form.Item>
                            <Button
                              onClick={() => remove(field.name)}
                              type="text"
                              icon={<DeleteOutlined />}
                              style={{ marginTop: '30px' }}
                              title="删除此字段"
                            />
                          </div>
                        </Col>
                      ))}
                    </Row>
                  )}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<UserOutlined />}
                      style={{ color: '#1890ff', borderColor: '#1890ff' }}
                      title="新增字段"
                    >
                      新增字段
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>

          {/* 检测项目字段区域 */}
          <div className="field-section">
            <Divider orientation="left" style={{ color: '#1890ff', fontSize: '14px', fontWeight: '600' }}>检测项目字段</Divider>
            <Paragraph className="field-instruction" style={{ marginBottom: '16px', fontSize: '12px', color: '#8c8c8c' }}>
              请设置检测项目相关字段（均为必填项）
            </Paragraph>
            
            {/* 检测项目字段 - 只有项目名称和结果 */}
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <FormItem 
                  name={['testItemFields', 0]}
                  label={
                    <span style={{ fontSize: '13px', color: '#262626' }}>
                      项目名称 <span style={{ color: '#8c8c8c' }}>(表头字段)</span>
                    </span>
                  }
                  tooltip="报告中表示检测项目名称的字段,不是填写检测项，而是填写表头字段，默认会提取所有检测项目"
                  rules={[{ required: true, message: '请输入项目名称字段' }]}
                >
                  <Input 
                    placeholder="项目名称"
                    style={{ fontSize: '13px' }}
                    onChange={(e) => handleInputChange(e, (value) => {
                      const currentValues = form.getFieldValue('testItemFields') || [];
                      currentValues[0] = value;
                      form.setFieldValue('testItemFields', currentValues);
                    })}
                    onBlur={(e) => handleInputBlur(e, (value) => {
                      const currentValues = form.getFieldValue('testItemFields') || [];
                      currentValues[0] = value;
                      form.setFieldValue('testItemFields', currentValues);
                    })}
                  />
                </FormItem>
              </Col>
              <Col xs={24} md={12}>
                <FormItem
                  name={['testItemFields', 1]}
                  label={
                    <span style={{ fontSize: '13px', color: '#262626' }}>
                      结果 <span style={{ color: '#8c8c8c' }}>(表头字段)</span>
                    </span>
                  }
                  tooltip="报告中表示检测结果的字段"
                  rules={[{ required: true, message: '请输入结果字段' }]}
                >
                  <Input 
                    placeholder="结果"
                    style={{ fontSize: '13px' }}
                    onChange={(e) => handleInputChange(e, (value) => {
                      const currentValues = form.getFieldValue('testItemFields') || [];
                      currentValues[1] = value;
                      form.setFieldValue('testItemFields', currentValues);
                    })}
                    onBlur={(e) => handleInputBlur(e, (value) => {
                      const currentValues = form.getFieldValue('testItemFields') || [];
                      currentValues[1] = value;
                      form.setFieldValue('testItemFields', currentValues);
                    })}
                  />
                </FormItem>
              </Col>
            </Row>
          </div>
        </Form>
      </div>
      </Card>
      <div 
        className={`upload-area ${dragOver ? 'drag-over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          e.stopPropagation()
          // 只有在已登录且未禁用时才显示拖拽效果
          if (!disabled && isAuthenticated) {
            setDragOver(true)
          }
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setDragOver(false)
        }}
        onDrop={handleFileDrop}
      >
        {selectedFiles.length === 0 ? (
          <div 
            className="upload-dragger-wrapper"
            onClick={!isAuthenticated ? () => message.warning('请先登录后再上传文件') : handleFileSelect}
          >
            <div className="upload-dragger-content">
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">拖放文件到此处或点击选择文件</p>
              <p className="ant-upload-hint">
                支持 PDF 文件和图像文件 (JPG, PNG)，可选择多个文件一起处理
              </p>
              <Button 
                type="primary" 
                disabled={disabled || !isAuthenticated}
                className="upload-button"
                onClick={(e) => {
                  e.stopPropagation(); // 阻止事件冒泡
                  if (!isAuthenticated) {
                    message.warning('请先登录后再上传文件');
                  } else {
                    handleFileSelect();
                  }
                }}
              >
                {!isAuthenticated ? '请先登录' : '选择文件'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="file-list-container">
            <div className="file-list-header">
              <Text strong>已选择文件 ({selectedFiles.length})</Text>
              <Space>
                <Button 
                  type="text" 
                  onClick={(e) => {
                    e.stopPropagation(); // 阻止事件冒泡
                    if (!isAuthenticated) {
                      message.warning('请先登录后再上传文件');
                    } else {
                      handleFileSelect();
                    }
                  }}
                  disabled={disabled || !isAuthenticated}
                  icon={<PlusOutlined />}
                >
                  {!isAuthenticated ? '请先登录' : '添加文件'}
                </Button>
                <Button 
                  type="text" 
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    // 清理所有预览URL
                    selectedFiles.forEach(file => {
                      if (typeof file === 'object' && file.name) {
                        // 找到对应的预览URL并清理
                        const objectURL = URL.createObjectURL(file);
                        URL.revokeObjectURL(objectURL);
                      }
                    });
                    
                    onClearFiles();
                    resetAllStates();
                  }}
                  disabled={disabled}
                  danger
                >
                  清空全部
                </Button>
              </Space>
            </div>
            
            {/* 文件预览说明 */}
            {selectedFiles.length > 0 && (
              <div style={{ marginBottom: '16px', padding: '8px 16px', backgroundColor: '#f0f9ff', borderRadius: '4px', fontSize: '12px', color: '#666' }}>
                <InfoCircleOutlined style={{ marginRight: '4px' }} />
                前5张图片将显示缩略图预览，其他文件以列表形式展示，点击 <EyeOutlined /> 图标可预览文件
              </div>
            )}
            
            <List
              className="file-list"
              itemLayout="horizontal"
              dataSource={selectedFiles}
              renderItem={(file, index) => {
                const fileName = typeof file === 'string' ? file.split(/[\\/]/).pop() : file.name
                const fileSize = typeof file === 'string' ? null : file.size
                const isImage = isImageFile(fileName)
                const shouldShowPreview = isImage && index < 5
                
                return (
                  <List.Item 
                    className="file-item"
                    style={{ padding: '16px' }}
                    actions={[
                      isImage && (
                        <Button
                          key="preview"
                          type="text"
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() => handlePreviewFile(file)}
                          title="预览文件"
                        />
                      ),
                      <Button
                        key="delete"
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveFile(file)}
                        disabled={disabled}
                        danger
                        title="删除此文件"
                      />
                    ].filter(Boolean)}
                  >
                    <List.Item.Meta
                      avatar={
                        shouldShowPreview ? (
                          <div style={{ width: '64px', height: '64px', borderRadius: '4px', overflow: 'hidden' }}>
                            <Image
                              src={typeof file === 'object' ? URL.createObjectURL(file) : file}
                              alt={fileName}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              preview={false}
                            />
                          </div>
                        ) : (
                          getFileIcon(file)
                        )
                      }
                      title={
                        <div className="file-info">
                          <Text ellipsis className="file-name" title={fileName}>
                            {fileName}
                          </Text>
                          <div className="file-meta">
                            <Tag className="file-type">
                              {fileName.split('.').pop().toUpperCase()}
                            </Tag>
                            {fileSize && (
                              <Text type="secondary" className="file-size">
                                {(fileSize / 1024 / 1024).toFixed(2)} MB
                              </Text>
                            )}
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )
              }}
            />


            
            <Button
              type="primary"
              icon={isProcessing || isMasking || isStartingProcess ? <LoadingOutlined /> : <CloudUploadOutlined />}
              loading={isProcessing || isMasking || isStartingProcess}
              onClick={handleStartProcessing}
              disabled={isProcessing || isMasking || isStartingProcess || selectedFiles.length === 0}
              className="process-button"
              block
            >
              {isStartingProcess ? '启动中...' : 
               isMasking ? '打码中...' : 
               isProcessing ? '处理中...' : 
               '开始解析'}
            </Button>
            


            {/* 详细打码进度条 */}
            {isMasking && (
              <Card style={{ marginTop: '16px', marginBottom: '24px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <EyeInvisibleOutlined style={{ fontSize: '24px', color: '#fa8c16', marginRight: '8px' }} />
                    <span style={{ fontSize: '16px', fontWeight: 'bold' }}>正在打码第 {currentProcessingIndex + 1} 个文件...</span>
                  </div>
                  <Progress
                    percent={maskingProgress}
                    status={maskingProgress === 100 ? 'success' : 'active'}
                    strokeColor={{
                      '0%': '#fa8c16',
                      '100%': '#52c41a',
                    }}
                  />
                  
                  {/* 详细进度条 */}
                  <div style={{ marginTop: '16px', textAlign: 'left' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <Text strong>详细进度</Text>
                      <span style={{ float: 'right', fontSize: '12px', color: '#666' }}>
                        {processedCount}/{totalFilesToMask} 文件
                      </span>
                    </div>
                    <Progress 
                      percent={maskingDetailProgress} 
                      status={maskingDetailProgress === 100 ? 'success' : 'active'}
                      strokeColor={{
                        '0%': '#fa8c16',
                        '100%': '#52c41a',
                      }}
                      showInfo={true}
                      format={(percent) => `${percent}%`}
                    />
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                      {maskingDetailStatus && (
                        <div>
                          <LoadingOutlined spin style={{ marginRight: '8px' }} />
                          {maskingDetailStatus}
                        </div>
                      )}
                      {maskingStep && (
                        <div style={{ marginTop: '4px', color: '#1890ff' }}>
                          当前步骤: {maskingStep}
                        </div>
                      )}
                      {currentMaskingFile && (
                        <div style={{ marginTop: '4px', color: '#52c41a' }}>
                          当前文件: {currentMaskingFile}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div style={{ marginTop: '12px', color: '#666' }}>
                    正在识别和遮挡敏感信息，保护您的隐私
                  </div>
                  <div style={{ marginTop: '8px', color: '#fa8c16', fontSize: '12px', fontWeight: 'bold' }}>
                    ⚠️ 打码期间需要保持本页面前置，切换到其它页面，会导致打码暂停
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* 积分消耗分析区域 - 实时更新 */}
      {((maskedFiles.length > 0) || (isMasking && selectedFiles.length > 0)) && memberInfo && (
        <div style={{ marginTop: '24px' }}>
          <Card>
            {/* 积分消耗详细分析 */}
            <div style={{ 
              padding: '16px', 
              background: '#f0f9ff', 
              borderRadius: '8px',
              border: '1px solid #bae7ff'
            }}>
              <div style={{ marginBottom: '12px' }}>
                <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>📊 积分消耗分析</Text>
                {isMasking && (
                  <Text style={{ marginLeft: '8px', color: '#fa8c16', fontSize: '12px' }}>
                    (实时更新中...)
                  </Text>
                )}
              </div>
              <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                <div style={{ marginBottom: '6px' }}>
                  <Text>
                    • 已分析图片数量：<Text strong style={{ color: '#fa8c16' }}>{maskedFiles.length}</Text> 张
                    {isMasking && currentProcessingIndex >= 0 && (
                      <Text style={{ color: '#1890ff', marginLeft: '8px' }}>
                        (正在处理第 {currentProcessingIndex + 1} 个文件)
                      </Text>
                    )}
                  </Text>
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <Text>
                    • 预计消耗积分：<Text strong style={{ color: '#f5222d' }}>{maskedFiles.length}</Text> 个
                  </Text>
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <Text>
                    • 您当前积分余额：<Text strong style={{ color: '#52c41a' }}>{memberInfo.bean_coins}</Text> 个
                  </Text>
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <Text>
                    • 会员等级：<Text strong style={{ color: '#1890ff' }}>{memberInfo.member_level_name || '普通用户'}</Text>
                    {memberInfo.current_discount_rate && !isNaN(memberInfo.current_discount_rate) && memberInfo.current_discount_rate < 1.0 && (
                      <Text style={{ color: '#fa8c16', marginLeft: '8px' }}>
                        (享受{((1 - memberInfo.current_discount_rate) * 100).toFixed(1)}%折扣)
                      </Text>
                    )}
                  </Text>
                </div>
                <div style={{ marginBottom: '6px' }}>
                  <Text>
                    • 处理完成后剩余：<Text strong style={{ 
                      color: (memberInfo.bean_coins - maskedFiles.length) >= 0 ? '#52c41a' : '#f5222d' 
                    }}>
                      {memberInfo.bean_coins - maskedFiles.length}
                    </Text> 个积分
                  </Text>
                </div>
                
                {/* 积分状态提示 */}
                <div style={{ marginTop: '12px' }}>
                  {(memberInfo.bean_coins - maskedFiles.length) >= 0 ? (
                    <Tag color="success" style={{ fontSize: '13px', padding: '4px 12px' }}>
                      ✅ 积分充足，可以继续处理
                    </Tag>
                  ) : (
                    <Tag color="error" style={{ fontSize: '13px', padding: '4px 12px' }}>
                      ❌ 积分不足，缺少 {maskedFiles.length - memberInfo.bean_coins} 个积分
                    </Tag>
                  )}
                </div>
                
                {/* PDF文件统计 */}
                {(() => {
                  const pdfPages = maskedFiles.filter(f => f.isPdfPage);
                  const originalFiles = selectedFiles.length;
                  if (pdfPages.length > 0) {
                    return (
                      <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                        <Text type="secondary">
                          ℹ️ 原始 {originalFiles} 个文件中包含PDF，已展开为 {maskedFiles.length} 张图片进行处理
                        </Text>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* 打码预览区域 */}
      {maskedFiles.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <Card title="📷 打码预览 - 逐张处理">
            <div style={{ marginBottom: '16px' }}>
              <Text type="secondary">
                以下是已完成打码处理的文件，您可以预览打码效果并逐张上传处理。
              </Text>
              
              {maskedFiles.length > 5 && (
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    共 {maskedFiles.length} 个文件，{showAllPreviews ? '已显示全部' : `默认显示前 5 个，其余 ${maskedFiles.length - 5} 个`}
                  </Text>
                  <Button 
                    type="link" 
                    size="small" 
                    onClick={() => setShowAllPreviews(!showAllPreviews)}
                    style={{ marginLeft: '8px', padding: '0' }}
                  >
                    {showAllPreviews ? '收起' : '查看全部'}
                  </Button>
                </div>
              )}
            </div>
            
            <List
              itemLayout="horizontal"
              dataSource={showAllPreviews ? maskedFiles : maskedFiles.slice(0, 5)}
              renderItem={(maskedFileInfo, index) => (
                <List.Item
                  style={{ padding: '16px', border: '1px solid #f0f0f0', borderRadius: '8px', marginBottom: '8px' }}
                  actions={[
                    maskedFileInfo.previewUrl && (
                      <Button
                        key="preview"
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => {
                          setPreviewVisible(true);
                          // 构建完整的标题，包含文件名和页面信息
                          const fileName = maskedFileInfo.originalFile?.name || maskedFileInfo.name;
                          const fullTitle = maskedFileInfo.isPdfPage 
                            ? `${fileName} - 第${maskedFileInfo.pageNumber}页`
                            : fileName;
                          setPreviewTitle(fullTitle);
                          setPreviewImage(maskedFileInfo.previewUrl);
                        }}
                        title="预览打码效果"
                      >
                        预览
                      </Button>
                    ),
                    <Button
                      key="delete"
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        // 清理并移除文件
                        if (maskedFileInfo.previewUrl) {
                          try {
                            URL.revokeObjectURL(maskedFileInfo.previewUrl);
                          } catch (e) {
                            // 静默处理URL清理错误
                          }
                        }
                        setMaskedFiles(prev => prev.filter((_, i) => i !== index));
                        message.info('已删除该文件', 1);
                      }}
                      disabled={isProcessing || isBatchProcessing}
                      danger
                      title="删除此文件"
                    >
                      删除
                    </Button>
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    avatar={
                      <div style={{ 
                        width: '48px', 
                        height: '48px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        backgroundColor: maskedFileInfo.isMasked ? '#f6ffed' : maskedFileInfo.error ? '#fff2f0' : '#fafafa',
                        borderRadius: '8px',
                        border: `1px solid ${maskedFileInfo.isMasked ? '#b7eb8f' : maskedFileInfo.error ? '#ffccc7' : '#d9d9d9'}`
                      }}>
                        {maskedFileInfo.isMasked ? (
                          <EyeInvisibleOutlined style={{ fontSize: '20px', color: '#52c41a' }} />
                        ) : maskedFileInfo.isPdf ? (
                          <FilePdfOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                        ) : maskedFileInfo.error ? (
                          <ExclamationCircleOutlined style={{ fontSize: '20px', color: '#ff4d4f' }} />
                        ) : (
                          <FileImageOutlined style={{ fontSize: '20px', color: '#666' }} />
                        )}
                      </div>
                    }
                    title={
                      <div>
                        <Text strong style={{ fontSize: '14px' }}>
                          {/* 显示原始文件名 */}
                          {maskedFileInfo.originalFile?.name || maskedFileInfo.name}
                          {/* 如果是PDF页面，显示页面信息 */}
                          {maskedFileInfo.isPdfPage && ` - 第${maskedFileInfo.pageNumber}页`}
                        </Text>
                        <div style={{ marginTop: '4px' }}>
                          {maskedFileInfo.isMasked && (
                            <Tag color="green" size="small">敏感信息已打码</Tag>
                          )}
                          {maskedFileInfo.isPdf && (
                            <Tag color="blue" size="small">PDF文件</Tag>
                          )}
                          {maskedFileInfo.error && (
                            <Tag color="red" size="small">处理失败</Tag>
                          )}
                        </div>
                      </div>
                    }
                    description={
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {maskedFileInfo.originalWidth && maskedFileInfo.originalHeight && (
                          <div>尺寸: {maskedFileInfo.originalWidth}×{maskedFileInfo.originalHeight}</div>
                        )}
                        {maskedFileInfo.error && (
                          <div style={{ color: '#ff4d4f', marginTop: '4px' }}>
                            错误: {maskedFileInfo.error}
                          </div>
                        )}
                        {maskedFileInfo.isPdf && (
                          <div style={{ marginTop: '4px' }}>
                            此PDF文件将直接处理，无需打码
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
            
            {maskedFiles.length > 0 && (
              <div style={{ 
                marginTop: '16px', 
                textAlign: 'center',
                padding: '16px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae7ff'
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <Text strong>批量操作</Text>
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                  {maskedFiles.filter(f => f.isPdfPage).length > 0 && (
                    <div>📄 包含 {maskedFiles.filter(f => f.isPdfPage).length} 个PDF页面（已转换为图片）</div>
                  )}
                  <div>📤 所有文件将以图片格式上传到后端处理</div>
                </div>
                

                <Space>
                  <Button
                    type="primary"
                    icon={isBatchProcessing ? <LoadingOutlined /> : <CloudUploadOutlined />}
                    onClick={async () => {
                      if (isBatchProcessing) {
                        // 如果正在批量处理，则停止
                        batchProcessingStopRef.current = true;
                        message.warning('⏹️ 正在停止批量处理...', 2);
                        return;
                      }
                      
                      // 开始批量处理 - 使用新的统一任务流程
                      await processBatchMaskedFiles();
                    }}
                    disabled={isProcessing || maskedFiles.length === 0}
                    loading={isBatchProcessing}
                  >
                    {isBatchProcessing ? '⏹️ 停止处理' : `🚀 全部处理 (${maskedFiles.length})`}
                  </Button>
                  <Button
                    onClick={() => {
                      // 清理所有预览URL并清空列表
                      maskedFiles.forEach(info => {
                        if (info.previewUrl) {
                          try {
                            URL.revokeObjectURL(info.previewUrl);
                          } catch (e) {
                            // 静默处理URL清理错误
                          }
                        }
                      });
                      resetAllStates();
                      message.info('已清空所有预览', 1);
                    }}
                    disabled={isProcessing || isBatchProcessing}
                  >
                    清空预览
                  </Button>
                </Space>
              </div>
            )}
          </Card>
        </div>
      )}
    </Card>
    
    {/* 预览Modal */}
    <Modal
      open={previewVisible}
      title={previewTitle}
      onCancel={handleClosePreview}
      footer={null}
      width={800}
      style={{ top: 20 }}
      bodyStyle={{ padding: '16px', textAlign: 'center' }}
    >
      {previewImage ? (
        <Image
          src={previewImage}
          alt={previewTitle}
          style={{ maxWidth: '100%', maxHeight: '70vh' }}
        />
      ) : (
        <div style={{ padding: '60px 0', color: '#999' }}>
          <FilePdfOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
          <div>此文件不支持预览</div>
        </div>
      )}
    </Modal>
  </div>
  )
}

export default FileUploader